import { UiChart } from "@agent/UiChart";
import { useUiModelStore } from "@agent/uiModelStore";
import {
  Callout,
  CardFrame,
  DividerRule,
  FieldList,
  GridBlock,
  ItemList,
  OptionButton,
  PanelSection,
  TableBlock,
  toneStyle,
  UiBadge,
} from "@agent/UiPrimitives";
import type { UiNode, UiPayload } from "@agent/uiSchema";
import { usePrincipalKey } from "@auth/sessionStore";
import { Markdown } from "@components/Markdown";
import {
  Button,
  Checkbox,
  cn,
  Input,
  Select,
} from "@scribenote/brian-design-system";
import { useEffect } from "react";

type UiActionHandler = (
  action: string,
  data: Record<string, unknown>,
) => void | Promise<void>;

/** An explicit `variant` keeps a payload-carrying button an action button (e.g. a `danger` Remove). */
const isChoiceButton = (node: UiNode): boolean =>
  node.type === "button" &&
  node.variant === undefined &&
  (node.payload !== undefined || node.description !== undefined);

/** Recurses on any `children` rather than a container list, so a new node type can't escape the gate. */
const collectRequiredBinds = (node: UiNode): string[] => {
  switch (node.type) {
    case "input":
    case "select":
      return node.required ? [node.bind] : [];
    default:
      return "children" in node
        ? (node.children ?? []).flatMap(collectRequiredBinds)
        : [];
  }
};

type UiButtonNode = Extract<UiNode, { type: "button" }>;

const collectButtons = (node: UiNode): UiButtonNode[] =>
  node.type === "button"
    ? [node]
    : "children" in node
      ? (node.children ?? []).flatMap(collectButtons)
      : [];

/** `payload == null` matches the backend gate's "absent or JSON null"; zero or several candidates leave Enter inert. */
const findEnterTarget = (node: UiNode): UiButtonNode | null => {
  const candidates = collectButtons(node).filter(
    (button) => button.completes === true && button.payload == null,
  );
  return candidates.length === 1 ? candidates[0] : null;
};

const ROW_ALIGN = {
  start: "items-start",
  center: "items-center",
  end: "items-end",
  baseline: "items-baseline",
} as const;

// Colours live in TEXT_VARIANT_COLOR so a tone replaces the default rather than racing it in stylesheet order.
const TEXT_VARIANTS = {
  title: "font-display text-2xl font-bold",
  heading: "font-display text-lg font-bold",
  subheading: "text-annotation-caps",
  body: "",
  caption: "text-xs",
} as const;

const TEXT_VARIANT_COLOR: Record<keyof typeof TEXT_VARIANTS, string> = {
  title: "text-ink",
  heading: "text-ink",
  subheading: "text-annotation",
  body: "",
  caption: "text-annotation",
};

// Stable identity: a fresh `{}` per read never settles under useSyncExternalStore after `reset`.
const EMPTY_MODEL: Record<string, unknown> = {};

const hasValue = (value: unknown): boolean =>
  typeof value === "string" ? value.trim().length > 0 : value != null;

export const UiRenderer = ({
  payload,
  storeKey,
  onAction,
  readOnly = false,
  frameless = false,
}: {
  payload: UiPayload;
  /** Owning task/view uuid, not `render_id`, so cards never share input state. */
  storeKey: string;
  onAction: UiActionHandler;
  /** SavedViews: buttons inert; inputs stay editable but route nowhere. */
  readOnly?: boolean;
  /** Embed the root card's contents in the caller's own card; no inset or tint. */
  frameless?: boolean;
}) => {
  // Uuids are clinic-scoped: namespace here so the next user on a shared device never sees unsubmitted text.
  const modelKey = `${usePrincipalKey()}:${storeKey}`;

  const init = useUiModelStore((s) => s.init);
  useEffect(() => init(modelKey, payload.data), [modelKey, payload, init]);

  const answered = payload.submitted_action != null;

  const enterTarget = readOnly ? null : findEnterTarget(payload.root);

  if (frameless) {
    const root = payload.root;
    const children = root.type === "card" ? (root.children ?? []) : [root];
    const requiredBinds = collectRequiredBinds(root);
    return (
      <div className="flex flex-col gap-2 font-body text-ink-body">
        {children.map((child, i) => (
          <Node
            key={i}
            node={child}
            renderId={modelKey}
            cardId={storeKey}
            onAction={onAction}
            requiredBinds={requiredBinds}
            enterTarget={enterTarget}
            readOnly={readOnly}
            answered={answered}
          />
        ))}
      </div>
    );
  }

  return (
    <Node
      node={payload.root}
      renderId={modelKey}
      cardId={storeKey}
      onAction={onAction}
      requiredBinds={collectRequiredBinds(payload.root)}
      enterTarget={enterTarget}
      readOnly={readOnly}
      answered={answered}
    />
  );
};

const Node = ({
  node,
  renderId,
  cardId,
  onAction,
  requiredBinds,
  enterTarget,
  readOnly,
  answered,
}: {
  node: UiNode;
  /** Principal-scoped key into the model store. */
  renderId: string;
  /** Bare `storeKey` for the test id — keeps principal uuids out of the DOM. */
  cardId: string;
  onAction: UiActionHandler;
  requiredBinds: string[];
  enterTarget: UiButtonNode | null;
  readOnly: boolean;
  answered: boolean;
}) => {
  const model = useUiModelStore((s) => s.models[renderId] ?? EMPTY_MODEL);
  const setValue = useUiModelStore((s) => s.set);
  const reset = useUiModelStore((s) => s.reset);
  const submitting = useUiModelStore((s) => s.submitting[renderId] ?? false);
  const setSubmitting = useUiModelStore((s) => s.setSubmitting);

  const press = async (action: string, data: Record<string, unknown>) => {
    setSubmitting(renderId, true);
    try {
      await onAction(action, data);
      reset(renderId);
    } finally {
      setSubmitting(renderId, false);
    }
  };

  const renderChildren = (children: UiNode[] | undefined) =>
    (children ?? []).map((child, i) => (
      <Node
        key={i}
        node={child}
        renderId={renderId}
        cardId={cardId}
        onAction={onAction}
        requiredBinds={requiredBinds}
        enterTarget={enterTarget}
        readOnly={readOnly}
        answered={answered}
      />
    ));

  switch (node.type) {
    case "stack":
    case "row": {
      const className =
        node.type === "row"
          ? [
              "flex",
              ROW_ALIGN[node.align ?? "center"] ?? ROW_ALIGN.center,
              "gap-3",
              // Otherwise a child holds its min-content width and overflows the card.
              "[&>*]:min-w-0",
              node.wrap ? "flex-wrap" : "",
              node.justify === "between" ? "justify-between" : "",
            ]
              .filter(Boolean)
              .join(" ")
          : "flex flex-col gap-3";
      return <div className={className}>{renderChildren(node.children)}</div>;
    }

    case "grid":
      return (
        <GridBlock columns={node.columns === 3 ? 3 : 2}>
          {renderChildren(node.children)}
        </GridBlock>
      );

    case "section":
      return (
        <PanelSection title={node.title} icon={node.icon} tone={node.tone}>
          {renderChildren(node.children)}
        </PanelSection>
      );

    case "callout":
      return (
        <Callout tone={node.tone} title={node.title} icon={node.icon}>
          {renderChildren(node.children)}
        </Callout>
      );

    case "fields":
      return <FieldList items={node.items} />;

    case "list":
      return <ItemList items={node.items} divided={node.divided} />;

    case "divider":
      return <DividerRule />;

    case "text": {
      // `.ds-markdown p` inherits colour and size, so the wrapper's classes reach the prose.
      const tone = toneStyle(node.tone);
      const body = <Markdown>{node.text}</Markdown>;

      if (node.variant === "heading")
        return (
          <div
            className={`${TEXT_VARIANTS.heading} ${tone.text || "text-ink"}`}
          >
            {body}
          </div>
        );

      const variant = node.variant ?? "body";
      const className = `${TEXT_VARIANTS[variant] ?? ""} ${
        tone.text || TEXT_VARIANT_COLOR[variant] || ""
      }`.trim();
      return className ? <div className={className}>{body}</div> : body;
    }

    case "badge":
      return (
        <UiBadge
          variant={node.variant}
          icon={node.icon}
          text={node.text}
          dataTestId={`ui-badge-${node.variant ?? "default"}`}
        />
      );

    case "input":
      return (
        <Input
          data-testid={`ui-${node.bind}`}
          placeholder={node.placeholder}
          value={(model[node.bind] as string) ?? ""}
          disabled={answered}
          onChange={(e) => setValue(renderId, node.bind, e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
            e.preventDefault();
            // Click's gate, plus Enter in an empty field submits nothing.
            if (
              enterTarget === null ||
              submitting ||
              answered ||
              !hasValue(model[node.bind]) ||
              requiredBinds.some((bind) => !hasValue(model[bind]))
            )
              return;
            void press(enterTarget.action, { ...model });
          }}
        />
      );

    case "select":
      return (
        <Select
          data-testid={`ui-${node.bind}`}
          label={node.label}
          options={node.options}
          value={(model[node.bind] as string) ?? null}
          disabled={answered}
          onValueChange={(value) => setValue(renderId, node.bind, value)}
        />
      );

    case "checkbox":
      return (
        <Checkbox
          data-testid={`ui-${node.bind}`}
          label={node.text}
          checked={Boolean(model[node.bind])}
          disabled={answered}
          onChange={(e) => setValue(renderId, node.bind, e.target.checked)}
        />
      );

    case "button": {
      const data = { ...model, ...node.payload };

      if (isChoiceButton(node)) {
        // Never gated on required fields.
        const optionId =
          typeof node.payload?.selected_option_id === "string"
            ? node.payload.selected_option_id
            : node.action;
        return (
          <OptionButton
            text={node.text}
            description={node.description}
            disabled={submitting || readOnly || answered}
            dataTestId={`ui-option-${optionId}`}
            onPress={() => void press(node.action, data)}
          />
        );
      }

      // An empty required field would consume the card server-side with holes; `payload` buttons aren't form submits.
      const missingRequired =
        node.payload === undefined &&
        requiredBinds.some((bind) => !hasValue(model[bind]));
      const inert = submitting || missingRequired || readOnly || answered;

      // Matches ConfirmDialog's destructive outline; never a red fill.
      if (node.variant === "danger")
        return (
          <Button
            variant="outline"
            data-testid={`ui-action-${node.action}`}
            disabled={inert}
            onClick={() => void press(node.action, data)}
            className={cn(
              "font-mono uppercase tracking-[0.13em]",
              !inert && "border-ink-stopped text-ink-stopped",
            )}
          >
            {node.text}
          </Button>
        );

      return (
        <Button
          variant={node.variant === "secondary" ? "outline" : "primary"}
          data-testid={`ui-action-${node.action}`}
          disabled={inert}
          onClick={() => void press(node.action, data)}
        >
          {node.text}
        </Button>
      );
    }

    case "card": {
      return <CardFrame id={cardId}>{renderChildren(node.children)}</CardFrame>;
    }

    case "chart":
      return <UiChart node={node} />;
    case "table":
      return <TableBlock columns={node.columns} rows={node.rows} />;

    default:
      // Compile-time exhaustiveness; an unknown node from a newer backend renders nothing.
      return renderNothing(node);
  }
};

const renderNothing = (_node: never): null => null;
